import { COOKIES } from "@/constants";
import { addBreadcrumb } from "@sentry/react";

/**
 * Set a cookie with the given name and value
 * @param {string} sameSite - SameSite attribute (default: 'strict') | 'None
 */
export const setCookie = (
  name: string,
  value: any,
  options: {
    path?: string;
    domain?: string;
    secure?: boolean;
    sameSite?: string;
  }
) => {
  //None
  const {
    path = "/",
    domain = isLocal ? "localhost" : ".sarasanalytics.com",
    secure = true,
    sameSite = "strict",
  } = options;

  document.cookie =
    `${name}=${encodeURIComponent(JSON.stringify(value))}; ` +
    `path=${path}; ` +
    `domain=${domain}; ` +
    `SameSite=${sameSite}` +
    `${secure ? "; Secure" : ""}`;
};

/** Parse JSON with a fallback instead of throwing. Logs failures as Sentry breadcrumbs. */
export const safeJsonParse = <T>(value: string, fallback: T | null = null): T | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return fallback;
  }

  try {
    return JSON.parse(trimmed);
  } catch (e) {
    addBreadcrumb({
      category: "cookie",
      message: "JSON parse failed in safeJsonParse",
      level: "warning",
      data: {
        valueLength: trimmed.length,
        error: e instanceof Error ? e.message : String(e),
      },
    });
    return fallback;
  }
};

/**
 * Get a cookie value by name. Values written by `setCookie` are JSON-encoded,
 * so they are parsed on read. Returns `null` if missing, empty, or unparseable.
 */
export const getCookie = <T = unknown>(name: string): T | null => {
  if (typeof document === "undefined") {
    return null;
  }

  try {
    const cookies = document.cookie.split(";");
    const cookieName = encodeURIComponent(name);

    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.startsWith(`${cookieName}=`)) {
        const decoded = decodeURIComponent(cookie.substring(cookieName.length + 1));

        if (!decoded) {
          return null;
        }

        return safeJsonParse<T>(decoded, null);
      }
    }
    return null;
  } catch (e) {
    addBreadcrumb({
      category: "cookie",
      message: "getCookie failed",
      level: "warning",
      data: {
        cookieName: name,
        error: e instanceof Error ? e.message : String(e),
      },
    });
    return null;
  }
};

/**
 * Delete a cookie by name
 * @param {string} name - Cookie name
 * @param {Object} options - Cookie options
 * @param {string} options.path - Cookie path (default: '/')
 * @param {string} options.domain - Cookie domain (default: current hostname)
 */
export const deleteCookie = (name: any, options: any) => {
  const { path = "/", domain = window.location.hostname } = options;

  document.cookie =
    `${encodeURIComponent(name)}=; ` +
    `expires=Thu, 01 Jan 1970 00:00:00 GMT; ` +
    `path=${path}; ` +
    `domain=${domain}`;
};

const isLocal =
  location.hostname === "localhost" || location.hostname === "127.0.0.1";

export const updateAuthTokenCookie = (token: string) => {
  setCookie(COOKIES.AUTH_TOKEN, token, {
    path: "/",
    secure: true,
    sameSite: "strict",
    domain: isLocal ? "localhost" : window.location.hostname,
  });
};
